import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { stack, stackOffsetDiverging } from 'd3-shape'
import { useId, useMemo } from 'react'

import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'

const MIN_SEGMENT_HEIGHT = 14
const LABEL_OFFSET_X = 0

type HoverGroupLabelsProps = {
  data: AreaGraphElement[]
  layerKeys: string[]
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
  datetimes: Date[]
  hoveredHourIndex: number | null
  layerKeyToLabel: Map<string, string>
}

/**
 * Renders activation group code labels on bar segments for the hovered column only.
 * Labels appear to the right of left-half bars and to the left of right-half bars.
 */
function HoverGroupLabels({
  data,
  layerKeys,
  timeScale,
  valueScale,
  datetimes,
  hoveredHourIndex,
  layerKeyToLabel,
}: Readonly<HoverGroupLabelsProps>) {
  const labels = useMemo(() => {
    if (hoveredHourIndex === null || data.length === 0 || layerKeys.length === 0) return null

    const stackGenerator = stack<AreaGraphElement>()
      .offset(stackOffsetDiverging)
      .value((d, key) => d.layerData[key] ?? 0)
      .keys(layerKeys)

    const stackedData = stackGenerator(data)

    const barX = timeScale(datetimes[hoveredHourIndex])
    const interval = datetimes.length >= 2 ? datetimes[1].getTime() - datetimes[0].getTime() : 3_600_000
    const nextDt = datetimes[hoveredHourIndex + 1] ?? new Date(datetimes[hoveredHourIndex].getTime() + interval)
    const barWidth = timeScale(nextDt) - barX

    // Determine if bar is in the left or right half of the chart
    const [rangeStart, rangeEnd] = timeScale.range()
    const chartMidpoint = (rangeStart + rangeEnd) / 2
    const barCenter = barX + barWidth / 2
    const isLeftHalf = barCenter < chartMidpoint

    // Position labels on the opposite side of the bar
    const labelX = isLeftHalf ? barX + barWidth + LABEL_OFFSET_X : barX - LABEL_OFFSET_X
    const anchor: 'start' | 'end' = isLeftHalf ? 'start' : 'end'

    const items: Array<{ key: string; y: number; label: string }> = []

    for (let layerIdx = 0; layerIdx < stackedData.length; layerIdx++) {
      const layer = stackedData[layerIdx]
      const point = layer[hoveredHourIndex]
      if (!point) continue

      const label = layerKeyToLabel.get(layerKeys[layerIdx])
      if (!label) continue

      const y0 = valueScale(point[0])
      const y1 = valueScale(point[1])
      const segmentHeight = Math.abs(y1 - y0)

      if (segmentHeight < MIN_SEGMENT_HEIGHT) continue

      const centerY = Math.min(y0, y1) + segmentHeight / 2

      items.push({
        key: layerKeys[layerIdx],
        y: centerY,
        label,
      })
    }

    return { items, labelX, anchor }
  }, [data, layerKeys, timeScale, valueScale, datetimes, hoveredHourIndex, layerKeyToLabel])

  const baseId = useId()
  const clipId = baseId + '-hover-labels-clip'
  const filterId = baseId + '-hover-labels-bg'

  if (!labels || labels.items.length === 0) return null

  const [rangeStart, rangeEnd] = timeScale.range()
  const [vRangeBottom, vRangeTop] = valueScale.range()

  return (
    <g className="hover-group-labels" style={{ pointerEvents: 'none' }}>
      <defs>
        <clipPath id={clipId}>
          <rect height={vRangeBottom - vRangeTop} width={rangeEnd - rangeStart} x={rangeStart} y={vRangeTop} />
        </clipPath>
        <filter height="1.2" id={filterId} width="1.1" x="-0.05" y="-0.1">
          <feFlood floodColor="white" floodOpacity="0.85" result="bg" />
          <feMerge>
            <feMergeNode in="bg" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {labels.items.map((item) => (
          <text
            key={item.key}
            dominantBaseline="central"
            fill="#374151"
            filter={`url(#${filterId})`}
            fontSize="9"
            fontWeight="600"
            style={{ pointerEvents: 'none' }}
            textAnchor={labels.anchor}
            x={labels.labelX}
            y={item.y}
          >
            {item.label}
          </text>
        ))}
      </g>
    </g>
  )
}

export default HoverGroupLabels
